<template>
  <NewBsBox
    :bs="wrapperBs"
    as="label"
  >
    <NewBsBox
      ref="checkboxRef"
      :bs="{ 'cmp.checkbox': 'inputNative' }"
      :checked="currentChecked"
      :disabled="disabled"
      :value="value"
      as="input"
      type="checkbox"
      @change="handleChange"
    />
    <NewBsBox
      :bs="{ 'cmp.checkbox': 'control', ...controlBs }"
      class="checkbox-control"
    >
      <template v-if="currentChecked">
        <Icon
          :grid-size="10"
          :size="10"
          :icon-path="indeterminate ? minusPath : checkedPath"
          color="white"
        />
      </template>
    </NewBsBox>
    <NewBsBox :bs="labelStyle">
      <!-- @slot 自定义标签 -->
      <slot :label="label">
        {{ label }}
      </slot>
    </NewBsBox>
  </NewBsBox>
</template>

<script lang="ts">
  import {
    defineComponent, computed, watch, ref,
  } from 'vue'
  import {
    injectOptionsContext,
  } from '@xhs/yam-beer/compose/options/useOptions'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import Icon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'

  import initStyle from './style'

  initStyle()

  const checkedPath = ['M9.225 1.671a.469.469 0 00-.662.035L3.31 7.539 1.444 5.467a.469.469 0 00-.697.627l2.209 2.453a.469.469 0 00.48.136.467.467 0 00.233-.141l5.59-6.209a.469.469 0 00-.034-.662z']
  const minusPath = ['M9.5 4.5h-9a.5.5 0 000 1h9a.5.5 0 000-1z']

  export default defineComponent({
    name: 'BeerCheckbox',
    components: {
      Icon,
      NewBsBox,
    },
    props: {
      /**
       * 半选中状态，全选场景使用
       * @see 例子:全选 #全选
       */
      indeterminate: {
        type: [Boolean],
        default: false,
      },
      /**
       * 是否初始选中
       * @type {true|false}
       */
      checked: {
        type: [Boolean],
        default: false,
      },
      /**
       * v-model 的值
       */
      modelValue: {
        type: [Array, Boolean],
        default: undefined,
      },
      /**
       * 选项的值
       */
      value: {
        type: [String, Number, Boolean, Object],
        default: true,
      },
      /**
       * 框框的标签
       */
      label: {
        type: String,
        required: false,
      },
      /**
       * 选项是否可选
       * @type {true|false}
       * @see style
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * @ignore
       *  wrapper bs to override default
       */
      bs: {
        type: Object,
        default: () => ({ }),
      },
      /**
       * @ignore
       */
      controlBs: {
        type: Object,
        default: () => ({ }),
      },
      /**
       *  label bs to override default
       * @ignore
       */
      labelBs: {
        type: Object,
        default: () => ({ }),
      },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const checkboxRef = ref<HTMLInputElement|null>(null)

      const [context, isFallback] = injectOptionsContext(
        'checkboxgroup',
        props.value,
        props.modelValue,
      )

      const modelValue = computed(() => {
        if (isFallback) {
          return props.modelValue
        }
        return context.modelValue.value
      })

      const currentChecked = computed(() => {
        if (props.indeterminate) {
          return true
        }
        if (Array.isArray(modelValue.value)) {
          if (!isFallback) {
            return context.activeIds.value.indexOf(
              context.valueToItem(props.value).id,
            ) > -1
          }
          return modelValue.value.indexOf(props.value) > -1
        }
        return !!modelValue.value
      })

      watch(currentChecked, checked => {
        if (!checkboxRef.value) {
          return
        }
        checkboxRef.value.checked = checked
      }, {
        immediate: true,
      })

      function emitVal(value: any) {
        emit('update:modelValue', value)
        if (!isFallback) {
          context.onChange(
            context.valueToItem(props.value),
          )
        }
      }
      function handleChange(e: Event) {
        const target = (e.target as HTMLInputElement)
        if (Array.isArray(modelValue.value)) {
          if (modelValue.value.indexOf(props.value) < 0) {
            emitVal(modelValue.value.concat([props.value]))
          } else {
            emitVal(modelValue.value.filter(i => i !== props.value))
          }
        } else {
          emitVal(!modelValue.value)
        }
        target.checked = currentChecked.value
      }
      return {
        checkboxRef,
        checkedPath,
        minusPath,
        currentChecked,
        handleChange,
        wrapperBs: computed(() => ({
          'cmp.checkbox': 'wrapper',
          ...props.bs,
        })),
        labelStyle: computed(() => ({
          'cmp.checkbox': 'label',
          ...props.labelBs,
        })),
      }
    },
  })
</script>
